<template>
  <breadcrumb-layout
    :org-id="orgId"
    :bread-crumb-items="breadCrumbItems"
    :show-new-goal-button="!team || !team.isArchived"
    :is-archived="goal && goal.isArchived"
    :timeline="goal && goal.timeline"
    :match-exact-interval="true"
    :team-id="team && team.id"
    @back="onBack"
  >
    <div class="flex-column goal-page" ref="listContainer">
      <div class="goal-wrapper">
        <goal-viewer
          v-if="goal"
          :goal-id="goal.id"
          :expand="true"
          :can-toggle-expansion="false"
          :show-alignment="true"
          :show-details-page-navigator="false"
          :render-krs-onload="true"
          :show-child-goals="false"
          :source="source"
          ref="goal"
          view-type="detail"
          @delete-goal="goalDeleted"
          @archive-goal="refreshEvents"
          @unarchive-goal="refreshEvents"
          @reopen-goal="refreshEvents"
          @kr-archived="refreshEvents"
          @kr-unarchived="refreshEvents"
          @kr-awarded="refreshEvents"
          @kr-reopened="refreshEvents"
          @goal-awarded="refreshEvents"
        ></goal-viewer>
      </div>
      <goal-activity-and-child-goals
        :event-to-focus="$route.query.event"
        v-if="goal"
        :goal="goal"
      />
    </div>
    <bulk-action-tray />
  </breadcrumb-layout>
</template>

<script>
import { trackEvent } from '@/analytics'
import BreadcrumbLayout from '@/components/BreadcrumbLayout'
import BulkActionTray from '@/components/bulk-actions/BulkActionTray.vue'
import GoalActivityAndChildGoals from '@/components/GoalActivityAndChildGoals'
import GoalViewer from '@/components/GoalViewer'
import { EVENT_ACTION, EVENT_CATEGORY, EVENT_NAME, SOURCE } from '@/constants/analytics'
import { ENTITY_TYPE } from '@/constants/entities'
import events from '@/constants/events'
import EventBus from '@/EventBus'
import BulkActionsMixin from '@/mixins/BulkActionsMixin'
import EventBusMixin from '@/mixins/EventBusMixin'
import GoalMixin from '@/mixins/GoalMixin'
import SubOrgIdTrackerMixin from '@/mixins/SubOrgIdTrackerMixin'
import TeamMixin from '@/mixins/TeamMixin'
import * as keymapService from '@/services/keymapService/keymapService'
import getDocumentTitle from '@/utils/getDocumentTitle'
import { getTeamPageRouteConfigFromTeamInfo } from '@/utils/navigationConfigs'
import removeQueriesFromRoute from '@/utils/removeQueriesFromRoute'
import selectFirstItem from '@/utils/selectFirstItem'
import showAPIError from '@/utils/showAPIError'
import URLify from '@/utils/URLify'
import * as R from 'ramda'
import { mapActions } from 'vuex'

export default {
  name: 'Goal',
  components: {
    'goal-viewer': GoalViewer,
    GoalActivityAndChildGoals,
    BreadcrumbLayout,
    BulkActionTray
  },
  data() {
    return {
      source: SOURCE.GOAL_PAGE
    }
  },
  props: ['orgId', 'goalId', 'krId'],
  mixins: [GoalMixin, EventBusMixin, TeamMixin, SubOrgIdTrackerMixin, BulkActionsMixin],
  computed: {
    goal() {
      return this.$store.getters.goalById(this.goalId)
    },
    org() {
      return this.$store.getters.orgById(this.orgId)
    },
    teamId() {
      return this.goal?.teamId
    },
    team() {
      return this.$store.getters.teamById(this.teamId)
    },
    goalRootEntityLocation() {
      return getTeamPageRouteConfigFromTeamInfo(this.team)
    },
    breadCrumbItems() {
      if (!this.goal || !this.team) return []
      return [
        {
          text: this.team.name,
          to: this.goalRootEntityLocation
        },
        {
          text: R.path(['goal', 'title'], this),
          type: ENTITY_TYPE.GOAL
        }
      ]
    },
    subOrgId() {
      return this.getClosestOrgLikeTeamId(this.teamId)
    }
  },
  methods: {
    ...mapActions(['getGoal']),
    goalDeleted() {
      this.$router.push(this.goalRootEntityLocation)
    },
    URLify,
    getChangedPercent(checkin) {
      let percent = ((checkin.newCurrent - checkin.oldCurrent) * 100) / checkin.oldCurrent
      return Math.round(percent)
    },
    selectFirstKR() {
      const krsContainer = this.$refs.listContainer.querySelector('.krs')
      selectFirstItem(krsContainer)
    },
    toggleItem: function(expanded) {
      const selectedItem = keymapService.selectedListItem(this.$refs.listContainer)
      if (
        selectedItem &&
        (!selectedItem.classList.contains('goal-container') ||
          selectedItem.classList.contains('type-child_goal') ||
          selectedItem.classList.contains('child-goal'))
      ) {
        let selectedItem = this.$refs.listContainer.querySelector('.selected')
        const vueNode = selectedItem.__vue__
        vueNode.expanded = typeof expanded === 'undefined' ? !vueNode.expanded : expanded
        setTimeout(() => selectedItem.classList.add('selected'), 100)
      }
    },
    expandItem() {
      this.toggleItem(true)
    },
    collapseItem() {
      this.toggleItem(false)
    },
    expandKR(krId) {
      const krRow = this.$el.querySelector(`[data-kr-id="${krId}"]`)
      krRow && krRow.__vue__ && (krRow.__vue__.expanded = true)
    },
    updateHistoryData() {
      EventBus.$emit('UPDATE_RECENT_PAGES_DATA', [
        [this.orgId, 'goalId', this.goalId, this.goal.title]
      ])
    },
    refreshEvents() {
      EventBus.$emit('RELOAD_ACTIVITY_EVENTS', this.goal.id)
    },
    pushAnalytics() {
      trackEvent(EVENT_CATEGORY.NAVIGATION, EVENT_ACTION.OPEN_PAGE, EVENT_NAME.GOAL)
    },
    handleAwardModalRendering() {
      const {
        query: { section }
      } = this.$route
      if (this.goal && section === 'give-award') {
        this.$refs.goal.showAwardModal()
        removeQueriesFromRoute.call(this, ['section'])
      }
    },
    onBack() {
      trackEvent(EVENT_CATEGORY.NAVIGATION, EVENT_ACTION.BACK_BUTTON, EVENT_NAME.GOAL)
    },
    async fetchGoal() {
      if (!this.goal?.parentGoalIds) {
        try {
          return await this.getGoal({
            orgId: this.orgId,
            goalId: this.goalId,
            options: { expand: 'parent_goal_ids' }
          })
        } catch (e) {
          const status = R.path(['response', 'status'], e)
          if (status === 404) {
            this.eventBusEmit('SHOW_404')
          } else {
            showAPIError(e)
          }
        }
      } else {
        return this.goal
      }
    },
    updateDocumentTitle() {
      const title = this.goal?.title
      if (title) {
        const org = this.getClosestOrgLikeTeam(this.team.id)
        document.title = getDocumentTitle({
          title,
          orgName: org.name
        })
      }
    }
  },
  async mounted() {
    keymapService.activateCategory('goal', {
      'previous-item': this.selectPreviousItem,
      'next-item': this.selectNextItem,
      'edit-item': this.editItem.bind(this),
      checkin: this.handleSpacebar.bind(this),
      'toggle-expansion': () => this.toggleItem(),
      open: this.viewItem,
      'expand-item': this.expandItem,
      'collapse-item': this.collapseItem,
      'cancel-editing': this.cancelEditing,
      'add-initiative': this.addInitiative,
      'delete-initiative': this.triggerDeleteInitiative,
      comment: this.comment
    })
    const goal = await this.fetchGoal()
    if (!goal) return
    this.$nextTick(function() {
      this.selectFirstKR()
      this.krId && this.expandKR(this.krId)
    })
    this.updateHistoryData()
    this.pushAnalytics()
    this.handleAwardModalRendering()
    this.listenToEventBus(events.GOAL_CLOSED, this.refreshEvents)
    this.updateDocumentTitle()
  },
  beforeDestroy() {
    keymapService.deactivateCategory('goal')
  },
  beforeRouteUpdate(ignoreThis, ignoreThisToo, next) {
    this.pushAnalytics()
    next()
  },
  watch: {
    goal(after, before) {
      if (before && after) {
        this.updateHistoryData()
      }
      this.updateDocumentTitle()
    },
    goalId: 'fetchGoal'
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/styleConfigurations';
@import '~@/styles/goals';

.kr-title {
  color: #8a8a8a;
}

/deep/ .comment-textarea-field {
  font-size: 14px;
}

.inline-comment-error {
  font-size: 0.875rem;
  padding-left: 22px;
  padding-right: 0;
}

.goal-wrapper > .row {
  background: #fff;
}

/deep/ .goal-title-container .icons-list {
  margin-top: 4px;
}

.goal-wrapper {
  margin-top: -15px;
  margin-bottom: 15px;
}

@include media-breakpoint-down(md) {
  /deep/ .goal-viewer-wrapper {
    box-shadow: none !important;
    border: none !important;
    border-radius: 0;
  }
  /deep/ .goal-viewer-wrapper .top-bar {
    margin: 0px -15px;
    border-radius: 0;
  }
  .goal-wrapper,
  .goal-page {
    padding: 0;
  }
  .goal-wrapper /deep/ .goal-container {
    border-bottom: 1px solid $borderPrimary !important;
  }
  /deep/ .goal-title-container {
    padding: 0 !important;
  }
  /deep/ .history-progress-container {
    padding-top: 5px;
  }
  /deep/ .kr-wrapper {
    padding: 0;

    .caret,
    .kr-icon-wrapper {
      display: none;
    }
  }
  /deep/ .goal-detail-spacer {
    width: 22px;
  }
  /deep/ .kr-title {
    padding-left: 4px;
  }
  .activity-discussion-container {
    margin: 0;
  }
  .activity-discussion {
    padding: 0;
    min-width: 0;
  }
  .goal-tabs-section {
    margin: 12px 0 !important;
  }
  .tabs {
    padding: 0;
  }
  /deep/ .child-goals {
    padding: 0;

    .skeleton-loader {
      padding-left: 0;
    }

    .child-goal {
      margin: 0;
    }
  }
  .goal-activity-add-comment {
    padding-bottom: 4px;
  }
  .goal-page {
    width: 100%;
  }
}

.goal-page {
  width: 91.66%;
}
</style>
